import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, input } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { Store, select } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, map, take, takeUntil } from "rxjs/operators";

import { PaginationOptions } from "@shared/constants/pagination";
import { getPagination } from "@store/index";
import { CustomerService } from "@shared/services/customer/customer.service";
import { Customer, DashboardData } from "@shared/models/customer.interface";
import { UpdateCustomerEntityGroupAction } from "src/app/store/actions/customerEntityGroupSelect.action";
import { AppState } from "src/app/store/models/state.model";
import { createCustomerLogoPath } from "@shared/utils/customer-logo-path.util";
import { ReportsService } from "@shared/services/reports/reports.service";

import {
	PayElectivePayGroupBeneficiaryData,
	PayElectivePayGroupBeneficiaryPagination
} from "src/app/shared/models/employee.interface";
import { MatLegacySlideToggleChange as MatSlideToggleChange } from "@angular/material/legacy-slide-toggle";
import { FeatureManagementService } from "@shared/services/feature-management/feature-management.service";
import { PaygroupsService } from "@shared/services/paygroups/paygroups.service";
import { FeatureFlagCodes } from "@shared/constants/feature-flag-codes";

@Component({
	selector: "app-customer-list",
	templateUrl: "./customer-list.component.html",
	styleUrls: ["./customer-list.component.scss"]
})
export class CustomerListComponent implements OnInit, OnDestroy {
	@Input() onlyApproved: boolean = true;
	@Input() isDashboard: boolean = false;
	@Input() payGroupStatuses: string = "";
	@Input() onlyWithLegalEnities: boolean = false;
	isCpNow = input(false);

	@Output() newCustomerSelectedEvent = new EventEmitter<Customer>();

	customers: Customer[] = [];
	pageSize: number = 10;
	pageIndex: number = 0;
	totalPageCount: number = 1;
	searchForm!: FormGroup;
	cpNowServicesForm!: FormGroup;
	destroy$ = new Subject<void>();
	payMethodsErrorCount: string[] = [];
	isCalendarPage = false;
	searchValue: string = "";
	paginationType: PaginationOptions = PaginationOptions.CUSTOMERS;
	fetchingCustomers = true;
	checkedToggle = false;
	isDestroyed = false;

	selectedCustomer!: Customer;
	hasCpNowIntegrationFeature = false;
	showCalendarErrorSidePanelButton = false;
	customerDisabledStates: { [customerId: string]: boolean } = {};

	constructor(
		private reportsService: ReportsService,
		private sanitizer: DomSanitizer,
		private customerService: CustomerService,
		private fb: FormBuilder,
		private store: Store<AppState>,
		private router: Router,
		private featureManagementService: FeatureManagementService,
		private payGroupService: PaygroupsService
	) {
		this.setFlagForCalendarPage();
	}

	ngOnInit(): void {
		this.initForm();
		this.setupPaginationSubscription();
		this.getFeatureFlags();
	}

	setupPaginationSubscription(): void {
		this.store.pipe(select(getPagination), takeUntil(this.destroy$)).subscribe({
			next: pagination => {
				if (pagination && pagination.hasOwnProperty("CUSTOMERS")) {
					const customersPagination = pagination["CUSTOMERS"];

					this.pageSize = customersPagination.pageSize;
					this.pageIndex = customersPagination.pageIndex;
					this.fetchingCustomers = true;
					this.searchCustomersWithPagination(
						this.searchValue,
						customersPagination.pageSize,
						customersPagination.pageIndex,
						this.payGroupStatuses
					);
				}
			}
		});
	}

	initForm(): void {
		this.searchForm = this.fb.group({
			search: new FormControl("")
		});

		this.searchForm
			.get("search")
			?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(400), distinctUntilChanged())
			.subscribe(searchValue => {
				this.searchValue = searchValue;
				this.pageIndex = 0;
				this.fetchingCustomers = true;
				this.searchCustomersWithPagination(searchValue, this.pageSize, this.pageIndex, this.payGroupStatuses);
			});
	}

	searchCustomersWithPagination(
		searchValue: string,
		pageSize: number,
		pageIndex: number,
		payGroupStatuses: string
	): void {
		this.customerService
			.searchCustomersWithPagination(searchValue, pageSize, pageIndex, payGroupStatuses)
			.pipe(
				map(res => {
					if (this.isDashboard) {
						res.items.filter(customer => {
							this.getReportData(customer.id).subscribe(res => {
								res.filter(item => (customer.dashboardData = item));
							});
						});
					}

					this.totalPageCount = res.totalPages;
					res.items.map(customer => createCustomerLogoPath(customer));
					return res;
				})
			)
			.subscribe(res => {
				this.customers = res.items;
				this.fetchingCustomers = false;

				if (this.isCpNow()) {
					this.loadCustomerDisabledStates();
				}
			});
	}

	showLogo(imagePath: string): SafeUrl {
		return this.sanitizer.bypassSecurityTrustUrl(imagePath);
	}

	selectCustomer(event: any, customer: Customer): void {
		this.store.dispatch(
			new UpdateCustomerEntityGroupAction({
				customerId: customer.id,
				customerName: customer.name,
				legalEntityId: "",
				payGroupId: ""
			})
		);

		if (event.target.classList.contains("mat-slide-toggle-bar")) {
			return;
		}

		this.newCustomerSelectedEvent.emit(customer);
	}

	getBeneficiariesReports(customerId: string): void {
		const paygroupId: string = "";
		this.reportsService
			.getBeneficiariesReports(paygroupId, customerId)
			.pipe(
				map((response: PayElectivePayGroupBeneficiaryPagination) => {
					return response.items;
				})
			)
			.subscribe((res: PayElectivePayGroupBeneficiaryData[]) => {
				if (res.length > 0 && res[0].statusCounts.INACTIVE > 0) this.payMethodsErrorCount.push(customerId);
			});
	}

	getReportData(customerId: string): Observable<DashboardData[]> {
		return this.customerService.getCustomersReportData(customerId);
	}

	ngOnDestroy(): void {
		this.isDestroyed = true;
		this.destroy$.next();
		this.destroy$.complete();
	}

	updateCustomer(cpNowValue: MatSlideToggleChange, customer: Customer): void {
		this.checkedToggle = true;
		customer = { ...customer, cloudPayNow: cpNowValue.checked };
		this.selectedCustomer = customer;
		this.customerService
			.updateCustomer(customer)
			.pipe(take(1))
			.subscribe(_ => this.setupPaginationSubscription());
	}

	getCustomerDisabledState(customerId: string): boolean {
		return this.customerDisabledStates[customerId] || false;
	}

	private setFlagForCalendarPage(): void {
		if (this.router.url === "/calendar") {
			this.isCalendarPage = true;
		}
	}

	private getFeatureFlags() {
		this.featureManagementService
			.hasFeature(FeatureFlagCodes.Q1_2026_WPAY_CPNOW_INTEGRATION)
			.pipe(take(1))
			.subscribe(hasFeature => {
				this.hasCpNowIntegrationFeature = hasFeature;
			});

		this.featureManagementService
			.hasFeature(FeatureFlagCodes.CALENDAR_ERROR)
			.pipe(take(1))
			.subscribe(hasFeature => {
				this.showCalendarErrorSidePanelButton = hasFeature;
			});
	}

	private loadCustomerDisabledStates(): void {
		if (!this.isCpNow() || this.isDestroyed || !this.router.url.includes("/cp-now")) {
			return;
		}

		this.customerDisabledStates = {};

		if (this.customers.length === 0) {
			return;
		}

		// Get all customer IDs at once
		const customerIds = this.customers.map(c => c.id);

		// Make ONE API call with all customer IDs
		this.payGroupService
			.getPayGroups(
				customerIds,
				[], // legalEntityIds
				"", // status
				-1, // pageSize
				0, // pageNumber
				"" // queryString
			)
			.pipe(take(1))
			.subscribe(res => {
				// Group pay groups by customer ID and check if any have cloudPayNow enabled
				const customerPayGroupMap = new Map<string, boolean>();

				res.items.forEach(payGroup => {
					const customerId = payGroup.customer.id; // adjust based on actual property name
					if (!customerPayGroupMap.has(customerId)) {
						customerPayGroupMap.set(customerId, false);
					}
					// If any paygroup for this customer has cloudPayNow, mark as disabled
					if (payGroup.data.cloudPayNow) {
						customerPayGroupMap.set(customerId, true);
					}
				});

				// Populate the dictionary for all customers
				this.customers.forEach(customer => {
					this.customerDisabledStates[customer.id] = customerPayGroupMap.get(customer.id) || false;
				});

				// Debug log to verify states
			});
	}
}
